import { writeBatch, doc, setDoc, updateDoc, getDoc, collection, query, where, getDocs, deleteDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { parseItineraryCsv } from '../utils/itineraryCsvParser';
import { parseItineraryJson } from '../utils/itineraryJsonParser';
import { compareItineraryData, generateUpdateBatches, getItineraryDocId } from '../utils/itineraryComparator';
import { UploadResult, ValidationError, Itinerary, ItinerarySummary, UploadProgress, InformationalMessage, ShipRecord } from '../types/itinerary';
import { CsvItineraryRow } from '../types/itinerary';
import { Cruise } from '../context/CruiseContext';
import { debug } from '../utils/debug';

const BATCH_SIZE = 500; // Firestore batch limit

/**
 * Upload itinerary file (CSV or JSON) and process updates
 * @param file - CSV or JSON file to upload
 * @param onProgress - Optional callback for progress updates
 */
export async function uploadItineraryFile(
  file: File,
  onProgress?: (progress: UploadProgress) => void
): Promise<UploadResult> {
  const startedAt = new Date();
  
  // Detect file type
  const isJson = file.name.toLowerCase().endsWith('.json') || file.type === 'application/json';
  const isCsv = file.name.toLowerCase().endsWith('.csv') || file.type === 'text/csv';
  
  if (!isJson && !isCsv) {
    return {
      success: false,
      newRecords: 0,
      updatedRecords: 0,
      newShipRecords: 0,
      updatedShipRecords: 0,
      errors: [{
        row: 0,
        message: 'Unsupported file type. Please upload a CSV or JSON file.'
      }],
      informationalMessages: [],
      summary: {
        totalFileRows: 0,
        headerRow: isCsv ? 1 : 0,
        processedRows: 0,
        skippedRows: 0,
        errorRows: 0,
        shipRows: 0,
        skippedShipRows: 0,
        errorShipRows: 0
      }
    };
  }
  
  let result: UploadResult;
  if (isJson) {
    result = await uploadItineraryJson(file, onProgress);
  } else {
    result = await uploadItineraryCsv(file, onProgress);
  }

  // Log to itineraryImportLogs in Firestore
  try {
    const logRef = doc(collection(db, 'itineraryImportLogs'));
    
    // Sanitize errors/messages to strip undefined fields
    const errors = (result.errors || []).map(e => ({
      row: e.row || 0,
      field: e.field || null,
      message: e.message || '',
      recordType: e.recordType || null
    }));
    
    const informationalMessages = (result.informationalMessages || []).map(m => ({
      type: m.type || '',
      cruiseLine: m.cruiseLine || '',
      shipName: m.shipName || '',
      oldShipName: m.oldShipName || null,
      urlType: m.urlType || null,
      url: m.url || null,
      oldUrl: m.oldUrl || null
    }));

    await setDoc(logRef, {
      type: 'manual',
      fileName: file.name,
      status: result.success ? 'success' : 'failed',
      startedAt,
      completedAt: new Date(),
      newRecords: result.newRecords || 0,
      updatedRecords: result.updatedRecords || 0,
      newShipRecords: result.newShipRecords || 0,
      updatedShipRecords: result.updatedShipRecords || 0,
      errors,
      informationalMessages,
      summary: {
        totalFileRows: result.summary?.totalFileRows || 0,
        processedRows: result.summary?.processedRows || 0,
        skippedRows: result.summary?.skippedRows || 0,
        errorRows: result.summary?.errorRows || 0,
        shipRows: result.summary?.shipRows || 0,
        skippedShipRows: result.summary?.skippedShipRows || 0,
        errorShipRows: result.summary?.errorShipRows || 0
      }
    });
  } catch (err) {
    console.error('[AdminService] Error saving import log:', err);
  }

  return result;
}

/**
 * Upload itinerary CSV file and process updates
 * @param file - CSV file to upload
 * @param onProgress - Optional callback for progress updates
 */
export async function uploadItineraryCsv(
  file: File,
  onProgress?: (progress: UploadProgress) => void
): Promise<UploadResult> {
  const result: UploadResult = {
    success: false,
    newRecords: 0,
    updatedRecords: 0,
    newShipRecords: 0,
    updatedShipRecords: 0,
    errors: [],
    informationalMessages: [],
    summary: {
      totalFileRows: 0,
      headerRow: 1, // Header is always row 1
      processedRows: 0,
      skippedRows: 0,
      errorRows: 0,
      shipRows: 0,
      skippedShipRows: 0,
      errorShipRows: 0
    }
  };

  try {
    debug.log('[AdminService] Starting CSV upload...');
    
    // Read file content
    onProgress?.({ stage: 'reading', message: 'Reading file...' });
    debug.log('[AdminService] Reading file content...');
    const csvContent = await readFileContent(file);
    debug.log('[AdminService] File content read, length:', csvContent.length);
    
    // Parse CSV
    onProgress?.({ 
      stage: 'parsing', 
      message: 'Parsing CSV file...',
      details: 'Validating data and converting dates'
    });
    debug.log('[AdminService] Parsing CSV...');
    const { data: csvData, errors: parseErrors, totalFileRows, errorRowNumbers } = parseItineraryCsv(csvContent);
    debug.log('[AdminService] CSV parsed:', { 
      totalFileRows, 
      dataRows: csvData.length, 
      errors: parseErrors.length,
      errorRows: errorRowNumbers.size
    });
    
    onProgress?.({ 
      stage: 'parsing', 
      message: `Parsed ${csvData.length} valid rows`,
      current: csvData.length,
      total: totalFileRows - 1, // Exclude header
      details: `${parseErrors.length} errors found`
    });
    
    result.errors.push(...parseErrors);
    result.summary.totalFileRows = totalFileRows;
    result.summary.errorRows = errorRowNumbers.size;
    
    if (csvData.length === 0) {
      debug.log('[AdminService] No valid data rows found');
      result.errors.push({
        row: 0,
        message: 'No valid data rows found in CSV file'
      });
      return result;
    }
    
    // Compare with existing data
    onProgress?.({ 
      stage: 'comparing', 
      message: 'Comparing with existing data...',
      current: 0,
      total: csvData.length,
      details: 'Checking for new and updated records'
    });
    debug.log('[AdminService] Comparing with existing data...');
    const comparisonResult = await compareItineraryData(csvData, (current, total) => {
      onProgress?.({
        stage: 'comparing',
        message: `Comparing records...`,
        current,
        total,
        details: `Processed ${current} of ${total} rows`
      });
    });
    debug.log('[AdminService] Comparison complete:', {
      new: comparisonResult.newItineraries.length,
      updated: comparisonResult.updatedItineraries.length,
      unchanged: comparisonResult.unchangedItineraries.length
    });
    
    // Generate update batches
    onProgress?.({ 
      stage: 'updating', 
      message: 'Preparing data updates...',
      details: `Found ${comparisonResult.newItineraries.length} new and ${comparisonResult.updatedItineraries.length} updated records`
    });
    debug.log('[AdminService] Generating update batches...');
    const { itineraryBatches, summaryBatches, oldDocIdsToDelete } = generateUpdateBatches(comparisonResult);
    debug.log('[AdminService] Batches generated:', {
      itineraries: itineraryBatches.length,
      summaries: summaryBatches.length,
      oldDocsToDelete: oldDocIdsToDelete.length
    });
    
    // Update ship metadata in cruiseMetadata collection
    onProgress?.({ 
      stage: 'updating', 
      message: 'Updating ship metadata...',
      details: 'Adding ships to cruise line lists'
    });
    debug.log('[AdminService] Updating ship metadata...');
    await updateShipMetadata(csvData, result.errors, result.informationalMessages);
    debug.log('[AdminService] Ship metadata updated');
    
    // Write itinerary batches (500 docs per batch)
    const totalBatches = Math.ceil(itineraryBatches.length / BATCH_SIZE);
    onProgress?.({ 
      stage: 'updating', 
      message: 'Writing itinerary data...',
      current: 0,
      total: itineraryBatches.length,
      details: `Writing ${itineraryBatches.length} itineraries in ${totalBatches} batches`
    });
    debug.log('[AdminService] Writing itinerary batches...');
    let processedItineraries = 0;
    
    for (let i = 0; i < itineraryBatches.length; i += BATCH_SIZE) {
      const batchNumber = Math.floor(i / BATCH_SIZE) + 1;
      debug.log(`[AdminService] Processing itinerary batch ${batchNumber}/${totalBatches}...`);
      
      const batch = writeBatch(db);
      const batchItems = itineraryBatches.slice(i, i + BATCH_SIZE);
      
      for (const item of batchItems) {
        const docRef = doc(db, 'itineraries', item.docId);
        batch.set(docRef, {
          ...item.data,
          lastUpdated: new Date()
        });
      }
      
      try {
        await batch.commit();
        processedItineraries += batchItems.length;
        debug.log(`[AdminService] Batch ${batchNumber} committed, processed: ${processedItineraries}`);
        
        onProgress?.({
          stage: 'updating',
          message: `Writing itinerary data...`,
          current: processedItineraries,
          total: itineraryBatches.length,
          details: `Batch ${batchNumber}/${totalBatches} complete`
        });
      } catch (error) {
        console.error(`[AdminService] Error writing batch ${batchNumber}:`, error);
        result.errors.push({
          row: 0,
          message: `Error writing batch ${batchNumber}: ${error instanceof Error ? error.message : 'Unknown error'}`
        });
      }
    }
    
    // Write summary batches
    onProgress?.({ 
      stage: 'updating', 
      message: 'Updating itinerary summaries...',
      details: `Updating ${summaryBatches.length} summaries`
    });
    debug.log('[AdminService] Writing summary batches...');
    for (let i = 0; i < summaryBatches.length; i++) {
      const item = summaryBatches[i];
      const docRef = doc(db, 'itinerarySummaries', item.docId);
      try {
        await setDoc(docRef, {
          ...item.data,
          lastUpdated: new Date()
        }, { merge: true });
        debug.log(`[AdminService] Summary updated for ${item.docId}`);
        
        if ((i + 1) % 10 === 0 || i === summaryBatches.length - 1) {
          onProgress?.({
            stage: 'updating',
            message: 'Updating itinerary summaries...',
            current: i + 1,
            total: summaryBatches.length,
            details: `Updated ${i + 1} of ${summaryBatches.length} summaries`
          });
        }
      } catch (error) {
        console.error(`[AdminService] Error updating summary for ${item.docId}:`, error);
        result.errors.push({
          row: 0,
          message: `Error updating summary for ${item.docId}: ${error instanceof Error ? error.message : 'Unknown error'}`
        });
      }
    }
    
    // Delete migrated legacy docs (batched where possible)
    if (oldDocIdsToDelete.length > 0) {
      onProgress?.({
        stage: 'updating',
        message: 'Removing migrated legacy documents...',
        details: `Deleting ${oldDocIdsToDelete.length} old-format documents`
      });
      debug.log(`[AdminService] Deleting ${oldDocIdsToDelete.length} migrated legacy docs...`);
      for (let i = 0; i < oldDocIdsToDelete.length; i += BATCH_SIZE) {
        const batch = writeBatch(db);
        const idsToDelete = oldDocIdsToDelete.slice(i, i + BATCH_SIZE);
        for (const oldDocId of idsToDelete) {
          batch.delete(doc(db, 'itineraries', oldDocId));
        }
        try {
          await batch.commit();
          debug.log(`[AdminService] Deleted ${idsToDelete.length} legacy docs`);
        } catch (error) {
          console.error('[AdminService] Error deleting legacy docs:', error);
          result.errors.push({
            row: 0,
            message: `Error deleting legacy documents: ${error instanceof Error ? error.message : 'Unknown error'}`
          });
        }
      }
    }
    
    debug.log('[AdminService] All batches written');
    
    // Phase 4 fan-out: Sync affected user cruises for updated official itineraries
    if (comparisonResult.updatedItineraries.length > 0) {
      onProgress?.({
        stage: 'updating',
        message: 'Syncing linked user cruises...',
        details: `Checking ${comparisonResult.updatedItineraries.length} updated itineraries`
      });
      for (const row of comparisonResult.updatedItineraries) {
        const docId = getItineraryDocId(row.cruiseLine, row.shipName, row.sailingDate, row.returnDate);
        try {
          await syncUserCruisesForOfficialItinerary(docId, row);
        } catch (syncErr) {
          console.error(`[AdminService] Error syncing user cruises for ${docId}:`, syncErr);
        }
      }
    }

    // Update result counts
    result.newRecords = comparisonResult.newItineraries.length;
    result.updatedRecords = comparisonResult.updatedItineraries.length;
    result.summary.processedRows = comparisonResult.newItineraries.length + comparisonResult.updatedItineraries.length;
    result.summary.skippedRows = comparisonResult.unchangedItineraries.length;
    result.summary.updatedFieldCounts = comparisonResult.updatedFieldCounts;
    result.success = result.errors.length === parseErrors.length; // Success if only parse errors
    
    // Verify totals match
    const calculatedTotal = result.summary.headerRow + result.summary.processedRows + result.summary.skippedRows + result.summary.errorRows;
    debug.log('[AdminService] Row count verification:', {
      totalFileRows: result.summary.totalFileRows,
      calculatedTotal,
      headerRow: result.summary.headerRow,
      processedRows: result.summary.processedRows,
      skippedRows: result.summary.skippedRows,
      errorRows: result.summary.errorRows
    });
    
  } catch (error) {
    console.error('[AdminService] Unexpected error:', error);
    result.errors.push({
      row: 0,
      message: `Unexpected error: ${error instanceof Error ? error.message : 'Unknown error'}`
    });
  }
  
    onProgress?.({ 
      stage: 'complete', 
      message: 'Upload complete!',
      details: `${result.newRecords} new, ${result.updatedRecords} updated`
    });
    
    debug.log('[AdminService] Upload complete:', {
      success: result.success,
      newRecords: result.newRecords,
      updatedRecords: result.updatedRecords,
      errors: result.errors.length
    });
    
    return result;
}

/**
 * Upload itinerary JSON file and process updates
 * @param file - JSON file to upload
 * @param onProgress - Optional callback for progress updates
 */
async function uploadItineraryJson(
  file: File,
  onProgress?: (progress: UploadProgress) => void
): Promise<UploadResult> {
  const result: UploadResult = {
    success: false,
    newRecords: 0,
    updatedRecords: 0,
    newShipRecords: 0,
    updatedShipRecords: 0,
    errors: [],
    informationalMessages: [],
    summary: {
      totalFileRows: 0,
      headerRow: 0, // No header row in JSON
      processedRows: 0,
      skippedRows: 0,
      errorRows: 0,
      shipRows: 0,
      skippedShipRows: 0,
      errorShipRows: 0
    }
  };

  try {
    debug.log('[AdminService] Starting JSON upload...');
    
    // Read file content
    onProgress?.({ stage: 'reading', message: 'Reading file...' });
    debug.log('[AdminService] Reading file content...');
    const jsonContent = await readFileContent(file);
    debug.log('[AdminService] File content read, length:', jsonContent.length);
    
    // Parse JSON
    onProgress?.({ 
      stage: 'parsing', 
      message: 'Parsing JSON file...',
      details: 'Validating data and converting dates'
    });
    debug.log('[AdminService] Parsing JSON...');
    const { data: jsonData, shipData, errors: parseErrors, totalFileRows, errorRowNumbers, skippedSummaryRows, shipErrorRowNumbers } = parseItineraryJson(jsonContent);
    debug.log('[AdminService] JSON parsed:', { 
      totalFileRows, 
      itineraryRows: jsonData.length,
      shipRows: shipData.length,
      errors: parseErrors.length,
      errorRows: errorRowNumbers.size,
      shipErrorRows: shipErrorRowNumbers.size,
      skippedSummaryRows
    });
    
    onProgress?.({ 
      stage: 'parsing', 
      message: `Parsed ${jsonData.length} itinerary rows and ${shipData.length} ship records`,
      current: jsonData.length + shipData.length,
      total: totalFileRows,
      details: `${parseErrors.length} errors found`
    });
    
    result.errors.push(...parseErrors);
    result.summary.totalFileRows = totalFileRows;
    result.summary.errorRows = errorRowNumbers.size;
    result.summary.shipRows = shipData.length;
    result.summary.errorShipRows = shipErrorRowNumbers.size;
    
    if (jsonData.length === 0 && shipData.length === 0) {
      debug.log('[AdminService] No valid data rows found');
      result.errors.push({
        row: 0,
        message: 'No valid data rows found in JSON file'
      });
      return result;
    }
    
    // Compare with existing data
    onProgress?.({ 
      stage: 'comparing', 
      message: 'Comparing with existing data...',
      current: 0,
      total: jsonData.length,
      details: 'Checking for new and updated records'
    });
    debug.log('[AdminService] Comparing with existing data...');
    const comparisonResult = await compareItineraryData(jsonData, (current, total) => {
      onProgress?.({
        stage: 'comparing',
        message: `Comparing records...`,
        current,
        total,
        details: `Processed ${current} of ${total} rows`
      });
    });
    debug.log('[AdminService] Comparison complete:', {
      new: comparisonResult.newItineraries.length,
      updated: comparisonResult.updatedItineraries.length,
      unchanged: comparisonResult.unchangedItineraries.length
    });
    
    // Generate update batches
    onProgress?.({ 
      stage: 'updating', 
      message: 'Preparing data updates...',
      details: `Found ${comparisonResult.newItineraries.length} new and ${comparisonResult.updatedItineraries.length} updated records`
    });
    debug.log('[AdminService] Generating update batches...');
    const { itineraryBatches, summaryBatches, oldDocIdsToDelete } = generateUpdateBatches(comparisonResult);
    debug.log('[AdminService] Batches generated:', {
      itineraries: itineraryBatches.length,
      summaries: summaryBatches.length,
      oldDocsToDelete: oldDocIdsToDelete.length
    });
    
    // Update ship metadata in cruiseMetadata collection (from itinerary records)
    onProgress?.({ 
      stage: 'updating', 
      message: 'Updating ship metadata from itinerary records...',
      details: 'Adding ships to cruise line lists'
    });
    debug.log('[AdminService] Updating ship metadata from itinerary records...');
    await updateShipMetadata(jsonData, result.errors, result.informationalMessages);
    debug.log('[AdminService] Ship metadata from itinerary records updated');
    
    // Process ship records separately
    if (shipData.length > 0) {
      onProgress?.({ 
        stage: 'updating', 
        message: 'Processing ship records...',
        details: `Updating ship metadata for ${shipData.length} ships`
      });
      debug.log('[AdminService] Processing ship records...');
      const shipResult = await processShipRecords(shipData, result.errors, result.informationalMessages);
      result.newShipRecords = shipResult.newRecords;
      result.updatedShipRecords = shipResult.updatedRecords;
      result.summary.skippedShipRows = shipResult.skippedRecords;
      debug.log('[AdminService] Ship records processed:', shipResult);
    }
    
    // Write itinerary batches (500 docs per batch)
    const totalBatches = Math.ceil(itineraryBatches.length / BATCH_SIZE);
    onProgress?.({ 
      stage: 'updating', 
      message: 'Writing itinerary data...',
      current: 0,
      total: itineraryBatches.length,
      details: `Writing ${itineraryBatches.length} itineraries in ${totalBatches} batches`
    });
    debug.log('[AdminService] Writing itinerary batches...');
    let processedItineraries = 0;
    
    for (let i = 0; i < itineraryBatches.length; i += BATCH_SIZE) {
      const batchNumber = Math.floor(i / BATCH_SIZE) + 1;
      debug.log(`[AdminService] Processing itinerary batch ${batchNumber}/${totalBatches}...`);
      
      const batch = writeBatch(db);
      const batchItems = itineraryBatches.slice(i, i + BATCH_SIZE);
      
      for (const item of batchItems) {
        const docRef = doc(db, 'itineraries', item.docId);
        batch.set(docRef, {
          ...item.data,
          lastUpdated: new Date()
        });
      }
      
      try {
        await batch.commit();
        processedItineraries += batchItems.length;
        debug.log(`[AdminService] Batch ${batchNumber} committed, processed: ${processedItineraries}`);
        
        onProgress?.({
          stage: 'updating',
          message: `Writing itinerary data...`,
          current: processedItineraries,
          total: itineraryBatches.length,
          details: `Batch ${batchNumber}/${totalBatches} complete`
        });
      } catch (error) {
        console.error(`[AdminService] Error writing batch ${batchNumber}:`, error);
        result.errors.push({
          row: 0,
          message: `Error writing batch ${batchNumber}: ${error instanceof Error ? error.message : 'Unknown error'}`
        });
      }
    }
    
    // Write summary batches
    onProgress?.({ 
      stage: 'updating', 
      message: 'Updating itinerary summaries...',
      details: `Updating ${summaryBatches.length} summaries`
    });
    debug.log('[AdminService] Writing summary batches...');
    for (let i = 0; i < summaryBatches.length; i++) {
      const item = summaryBatches[i];
      const docRef = doc(db, 'itinerarySummaries', item.docId);
      try {
        await setDoc(docRef, {
          ...item.data,
          lastUpdated: new Date()
        }, { merge: true });
        debug.log(`[AdminService] Summary updated for ${item.docId}`);
        
        if ((i + 1) % 10 === 0 || i === summaryBatches.length - 1) {
          onProgress?.({
            stage: 'updating',
            message: 'Updating itinerary summaries...',
            current: i + 1,
            total: summaryBatches.length,
            details: `Updated ${i + 1} of ${summaryBatches.length} summaries`
          });
        }
      } catch (error) {
        console.error(`[AdminService] Error updating summary for ${item.docId}:`, error);
        result.errors.push({
          row: 0,
          message: `Error updating summary for ${item.docId}: ${error instanceof Error ? error.message : 'Unknown error'}`
        });
      }
    }
    
    // Delete migrated legacy docs (batched where possible)
    if (oldDocIdsToDelete.length > 0) {
      onProgress?.({
        stage: 'updating',
        message: 'Removing migrated legacy documents...',
        details: `Deleting ${oldDocIdsToDelete.length} old-format documents`
      });
      debug.log(`[AdminService] Deleting ${oldDocIdsToDelete.length} migrated legacy docs...`);
      for (let i = 0; i < oldDocIdsToDelete.length; i += BATCH_SIZE) {
        const batch = writeBatch(db);
        const idsToDelete = oldDocIdsToDelete.slice(i, i + BATCH_SIZE);
        for (const oldDocId of idsToDelete) {
          batch.delete(doc(db, 'itineraries', oldDocId));
        }
        try {
          await batch.commit();
          debug.log(`[AdminService] Deleted ${idsToDelete.length} legacy docs`);
        } catch (error) {
          console.error('[AdminService] Error deleting legacy docs:', error);
          result.errors.push({
            row: 0,
            message: `Error deleting legacy documents: ${error instanceof Error ? error.message : 'Unknown error'}`
          });
        }
      }
    }
    
    debug.log('[AdminService] All batches written');
    
    // Update result counts for itinerary records
    result.newRecords = comparisonResult.newItineraries.length;
    result.updatedRecords = comparisonResult.updatedItineraries.length;
    result.summary.processedRows = comparisonResult.newItineraries.length + comparisonResult.updatedItineraries.length;
    result.summary.skippedRows = comparisonResult.unchangedItineraries.length + skippedSummaryRows; // Include skipped summary rows
    result.success = result.errors.length === parseErrors.length; // Success if only parse errors
    
    // Verify totals match (accounting for both record types)
    const calculatedTotal = result.summary.headerRow + 
                           result.summary.processedRows + 
                           result.summary.skippedRows + 
                           result.summary.errorRows +
                           result.summary.shipRows +
                           result.summary.skippedShipRows +
                           result.summary.errorShipRows;
    debug.log('[AdminService] Row count verification:', {
      totalFileRows: result.summary.totalFileRows,
      calculatedTotal,
      headerRow: result.summary.headerRow,
      processedRows: result.summary.processedRows,
      skippedRows: result.summary.skippedRows,
      errorRows: result.summary.errorRows,
      shipRows: result.summary.shipRows,
      skippedShipRows: result.summary.skippedShipRows,
      errorShipRows: result.summary.errorShipRows,
      skippedSummaryRows
    });
    
  } catch (error) {
    console.error('[AdminService] Unexpected error:', error);
    result.errors.push({
      row: 0,
      message: `Unexpected error: ${error instanceof Error ? error.message : 'Unknown error'}`
    });
  }
  
  onProgress?.({ 
    stage: 'complete', 
    message: 'Upload complete!',
    details: `${result.newRecords} new itineraries, ${result.updatedRecords} updated itineraries, ${result.newShipRecords + result.updatedShipRecords} ship records processed`
  });
  
  debug.log('[AdminService] Upload complete:', {
    success: result.success,
    newRecords: result.newRecords,
    updatedRecords: result.updatedRecords,
    newShipRecords: result.newShipRecords,
    updatedShipRecords: result.updatedShipRecords,
    errors: result.errors.length
  });
  
  return result;
}

/**
 * Read file content as text
 */
function readFileContent(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      resolve(e.target?.result as string);
    };
    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };
    reader.readAsText(file);
  });
}

/**
 * Process ship records and update ship metadata in cruiseMetadata collection
 */
async function processShipRecords(
  shipData: ShipRecord[],
  errors: ValidationError[],
  informationalMessages: InformationalMessage[]
): Promise<{ newRecords: number; updatedRecords: number; skippedRecords: number }> {
  let newRecords = 0;
  let updatedRecords = 0;
  let skippedRecords = 0;
  
  // Group ship records by cruise line and ship name
  const shipMetadataMap = new Map<string, ShipRecord>();
  
  for (const shipRecord of shipData) {
    const key = `${shipRecord.cruiseLine}_${shipRecord.shipName}`;
    // Use the latest record if duplicates exist
    shipMetadataMap.set(key, shipRecord);
  }
  
  // Fetch all cruiseMetadata documents to build a name-to-docId map
  debug.log('[AdminService] Fetching cruiseMetadata documents for ship record processing...');
  const metadataCollection = collection(db, 'cruiseMetadata');
  const metadataSnapshot = await getDocs(metadataCollection);
  const nameToDocIdMap = new Map<string, string>();
  
  metadataSnapshot.docs.forEach(doc => {
    const data = doc.data();
    if (data.name) {
      nameToDocIdMap.set(data.name, doc.id);
    }
  });
  
  debug.log(`[AdminService] Found ${nameToDocIdMap.size} cruiseMetadata documents`);
  
  // Process each ship record
  for (const [key, shipRecord] of shipMetadataMap.entries()) {
    const parts = key.split('_');
    const cruiseLineName = shipRecord.cruiseLine;
    const shipName = parts.slice(1).join('_'); // Handle ship names with underscores
    
    try {
      // Find the document ID by matching the name field
      const docId = nameToDocIdMap.get(cruiseLineName);
      
      if (!docId) {
        errors.push({
          row: 0,
          recordType: 'ship',
          message: `Cruise line "${cruiseLineName}" not found in cruiseMetadata collection (no document with name="${cruiseLineName}")`
        });
        continue;
      }
      
      const metadataRef = doc(db, 'cruiseMetadata', docId);
      const metadataDoc = await getDoc(metadataRef);
      
      if (metadataDoc.exists()) {
        const existingData = metadataDoc.data();
        
        // Get existing shipMetadata or create new
        const shipMetadata = existingData.shipMetadata || {};
        
        // Get existing ships array
        const ships = existingData.ships || [];
        
        // Find ship with case-insensitive matching
        const shipIndex = ships.findIndex((s: string) => s.toLowerCase() === shipName.toLowerCase());
        const existingShipName = shipIndex >= 0 ? ships[shipIndex] : null;
        
        // Check if ship metadata already exists
        const existingShipMetadata = shipMetadata[existingShipName || shipName] || {};
        
        // Build new ship metadata object
        const newShipMetadata: any = {
          ...existingShipMetadata
        };
        
        // Update URLs if provided
        if (shipRecord.shipLocation) {
          newShipMetadata.locationUrl = shipRecord.shipLocation;
        }
        if (shipRecord.shipUrl) {
          newShipMetadata.infoUrl = shipRecord.shipUrl;
        }
        if (shipRecord.shipDeckplan) {
          newShipMetadata.deckplanUrl = shipRecord.shipDeckplan;
        }
        
        // Update additional ship metadata fields
        if (shipRecord.yearBuilt) newShipMetadata.yearBuilt = shipRecord.yearBuilt;
        if (shipRecord.lastRefurbishment) newShipMetadata.lastRefurbishment = shipRecord.lastRefurbishment;
        if (shipRecord.shipClass) newShipMetadata.shipClass = shipRecord.shipClass;
        if (shipRecord.grossTonnage) newShipMetadata.grossTonnage = shipRecord.grossTonnage;
        if (shipRecord.decks) newShipMetadata.decks = shipRecord.decks;
        if (shipRecord.length) newShipMetadata.length = shipRecord.length;
        if (shipRecord.width) newShipMetadata.width = shipRecord.width;
        if (shipRecord.speed) newShipMetadata.speed = shipRecord.speed;
        if (shipRecord.passengers) newShipMetadata.passengers = shipRecord.passengers;
        if (shipRecord.crew) newShipMetadata.crew = shipRecord.crew;
        if (shipRecord.cabin) newShipMetadata.cabin = shipRecord.cabin;
        if (shipRecord.formerNames) newShipMetadata.formerNames = shipRecord.formerNames;
        if (shipRecord.builder) newShipMetadata.builder = shipRecord.builder;
        if (shipRecord.flagState) newShipMetadata.flagState = shipRecord.flagState;
        
        // Check if there are actual changes
        const hasChanges = JSON.stringify(newShipMetadata) !== JSON.stringify(existingShipMetadata);
        let needsUpdate = false;
        
        if (existingShipName) {
          // Ship exists, check if case needs to be updated
          if (existingShipName !== shipName) {
            // Case mismatch - update the ships array with correct case
            ships[shipIndex] = shipName;
            debug.log(`[AdminService] Updated ship name case from "${existingShipName}" to "${shipName}" for ${cruiseLineName}`);
            
            // Migrate metadata if needed
            if (shipMetadata[existingShipName]) {
              shipMetadata[shipName] = newShipMetadata;
              delete shipMetadata[existingShipName];
            } else {
              shipMetadata[shipName] = newShipMetadata;
            }
            
            informationalMessages.push({
              type: 'ship_case_corrected',
              cruiseLine: cruiseLineName,
              shipName: shipName,
              oldShipName: existingShipName
            });
            needsUpdate = true;
            // If there are metadata changes, count as update
            if (hasChanges) {
              updatedRecords++;
              informationalMessages.push({
                type: 'ship_metadata_updated',
                cruiseLine: cruiseLineName,
                shipName: shipName
              });
            }
          } else {
            // Same case, just update metadata if there are changes
            if (hasChanges) {
              shipMetadata[shipName] = newShipMetadata;
              needsUpdate = true;
              updatedRecords++;
              informationalMessages.push({
                type: 'ship_metadata_updated',
                cruiseLine: cruiseLineName,
                shipName: shipName
              });
            }
          }
        } else {
          // Ship doesn't exist, add it
          ships.push(shipName);
          shipMetadata[shipName] = newShipMetadata;
          debug.log(`[AdminService] Adding ship "${shipName}" to ships array for ${cruiseLineName}`);
          
          informationalMessages.push({
            type: 'ship_added',
            cruiseLine: cruiseLineName,
            shipName: shipName
          });
          newRecords++;
          needsUpdate = true;
        }
        
        if (needsUpdate) {
          await updateDoc(metadataRef, {
            shipMetadata,
            ships
          });
          
          debug.log(`[AdminService] Updated ship metadata for ${cruiseLineName} - ${shipName}`);
        } else {
          skippedRecords++;
          debug.log(`[AdminService] No changes needed for ${cruiseLineName} - ${shipName}`);
        }
      } else {
        errors.push({
          row: 0,
          recordType: 'ship',
          message: `Cruise line document "${docId}" not found in cruiseMetadata collection`
        });
      }
    } catch (error) {
      console.error(`[AdminService] Error processing ship record for ${key}:`, error);
      errors.push({
        row: 0,
        recordType: 'ship',
        message: `Error processing ship record for ${cruiseLineName} - ${shipName}: ${error instanceof Error ? error.message : 'Unknown error'}`
      });
    }
  }
  
  return { newRecords, updatedRecords, skippedRecords };
}

/**
 * Update ship metadata in cruiseMetadata collection
 * Matches CSV cruise line name to the 'name' field in cruiseMetadata documents
 */
async function updateShipMetadata(
  csvData: CsvItineraryRow[],
  errors: ValidationError[],
  informationalMessages: InformationalMessage[]
): Promise<void> {
  // Group by cruise line and ship name
  const shipMetadataMap = new Map<string, { cruiseLineName: string; locationUrl?: string; infoUrl?: string; deckplanUrl?: string }>();
  
  for (const row of csvData) {
    const key = `${row.cruiseLine}_${row.shipName}`;
    
    if (!shipMetadataMap.has(key)) {
      shipMetadataMap.set(key, { cruiseLineName: row.cruiseLine });
    }
    
    const metadata = shipMetadataMap.get(key)!;
    
    if (row.shipLocation && !metadata.locationUrl) {
      metadata.locationUrl = row.shipLocation;
    }
    
    if (row.shipUrl && !metadata.infoUrl) {
      metadata.infoUrl = row.shipUrl;
    }
    
    if (row.shipDeckplan && !metadata.deckplanUrl) {
      metadata.deckplanUrl = row.shipDeckplan;
    }
  }
  
  // Fetch all cruiseMetadata documents to build a name-to-docId map
  debug.log('[AdminService] Fetching cruiseMetadata documents for ship metadata update...');
  const metadataCollection = collection(db, 'cruiseMetadata');
  const metadataSnapshot = await getDocs(metadataCollection);
  const nameToDocIdMap = new Map<string, string>();
  
  metadataSnapshot.docs.forEach(doc => {
    const data = doc.data();
    if (data.name) {
      nameToDocIdMap.set(data.name, doc.id);
    }
  });
  
  debug.log(`[AdminService] Found ${nameToDocIdMap.size} cruiseMetadata documents`);
  
  // Update cruiseMetadata documents
  for (const [key, metadata] of shipMetadataMap.entries()) {
    const parts = key.split('_');
    const cruiseLineName = metadata.cruiseLineName; // Use the CSV cruise line name
    const shipName = parts.slice(1).join('_'); // Handle ship names with underscores
    
    try {
      // Find the document ID by matching the name field
      const docId = nameToDocIdMap.get(cruiseLineName);
      
      if (!docId) {
        errors.push({
          row: 0,
          message: `Cruise line "${cruiseLineName}" not found in cruiseMetadata collection (no document with name="${cruiseLineName}")`
        });
        continue;
      }
      
      const metadataRef = doc(db, 'cruiseMetadata', docId);
      const metadataDoc = await getDoc(metadataRef);
      
      if (metadataDoc.exists()) {
        const existingData = metadataDoc.data();
        
        // Get existing shipMetadata or create new
        const shipMetadata = existingData.shipMetadata || {};
        
        // Get existing ships array
        const ships = existingData.ships || [];
        
        // Find ship with case-insensitive matching
        const shipIndex = ships.findIndex((s: string) => s.toLowerCase() === shipName.toLowerCase());
        const existingShipName = shipIndex >= 0 ? ships[shipIndex] : null;
        
        if (existingShipName) {
          // Ship exists, check if case needs to be updated
          if (existingShipName !== shipName) {
            // Case mismatch - update the ships array with correct case
            ships[shipIndex] = shipName;
            debug.log(`[AdminService] Updated ship name case from "${existingShipName}" to "${shipName}" for ${cruiseLineName}`);
            
            // Add informational message
            informationalMessages.push({
              type: 'ship_case_corrected',
              cruiseLine: cruiseLineName,
              shipName: shipName,
              oldShipName: existingShipName
            });
            
            // If shipMetadata exists under old case, migrate it to new case
            if (shipMetadata[existingShipName]) {
              shipMetadata[shipName] = shipMetadata[existingShipName];
              // Remove old case entry
              delete shipMetadata[existingShipName];
            }
          }
        } else {
          // Ship doesn't exist, add it
          ships.push(shipName);
          debug.log(`[AdminService] Adding ship "${shipName}" to ships array for ${cruiseLineName}`);
          
          // Add informational message
          informationalMessages.push({
            type: 'ship_added',
            cruiseLine: cruiseLineName,
            shipName: shipName
          });
        }
        
        // Get existing ship metadata from Firebase (before any updates)
        const existingShipMetadata = shipMetadata[shipName] || {};
        
        // Update or create ship entry in shipMetadata
        if (!shipMetadata[shipName]) {
          shipMetadata[shipName] = {};
        }
        
        let hasUpdates = false;
        
        // Track URL updates and add informational messages
        if (metadata.locationUrl) {
          if (!existingShipMetadata.locationUrl) {
            // New URL added
            shipMetadata[shipName].locationUrl = metadata.locationUrl;
            hasUpdates = true;
            informationalMessages.push({
              type: 'ship_url_added',
              cruiseLine: cruiseLineName,
              shipName: shipName,
              urlType: 'locationUrl',
              url: metadata.locationUrl
            });
          } else if (existingShipMetadata.locationUrl !== metadata.locationUrl) {
            // URL updated
            const oldUrl = existingShipMetadata.locationUrl;
            shipMetadata[shipName].locationUrl = metadata.locationUrl;
            hasUpdates = true;
            informationalMessages.push({
              type: 'ship_url_updated',
              cruiseLine: cruiseLineName,
              shipName: shipName,
              urlType: 'locationUrl',
              url: metadata.locationUrl,
              oldUrl: oldUrl
            });
          }
        }
        
        if (metadata.infoUrl) {
          if (!existingShipMetadata.infoUrl) {
            // New URL added
            shipMetadata[shipName].infoUrl = metadata.infoUrl;
            hasUpdates = true;
            informationalMessages.push({
              type: 'ship_url_added',
              cruiseLine: cruiseLineName,
              shipName: shipName,
              urlType: 'infoUrl',
              url: metadata.infoUrl
            });
          } else if (existingShipMetadata.infoUrl !== metadata.infoUrl) {
            // URL updated
            const oldUrl = existingShipMetadata.infoUrl;
            shipMetadata[shipName].infoUrl = metadata.infoUrl;
            hasUpdates = true;
            informationalMessages.push({
              type: 'ship_url_updated',
              cruiseLine: cruiseLineName,
              shipName: shipName,
              urlType: 'infoUrl',
              url: metadata.infoUrl,
              oldUrl: oldUrl
            });
          }
        }
        
        if (metadata.deckplanUrl) {
          if (!existingShipMetadata.deckplanUrl) {
            // New URL added
            shipMetadata[shipName].deckplanUrl = metadata.deckplanUrl;
            hasUpdates = true;
            informationalMessages.push({
              type: 'ship_url_added',
              cruiseLine: cruiseLineName,
              shipName: shipName,
              urlType: 'deckplanUrl',
              url: metadata.deckplanUrl
            });
          } else if (existingShipMetadata.deckplanUrl !== metadata.deckplanUrl) {
            // URL updated
            const oldUrl = existingShipMetadata.deckplanUrl;
            shipMetadata[shipName].deckplanUrl = metadata.deckplanUrl;
            hasUpdates = true;
            informationalMessages.push({
              type: 'ship_url_updated',
              cruiseLine: cruiseLineName,
              shipName: shipName,
              urlType: 'deckplanUrl',
              url: metadata.deckplanUrl,
              oldUrl: oldUrl
            });
          }
        }
        
        // Only update document if there are actual changes (ships array changes or URL updates)
        if (hasUpdates || existingShipName !== shipName) {
          await updateDoc(metadataRef, {
            shipMetadata,
            ships
          });
          debug.log(`[AdminService] Updated ship metadata for ${cruiseLineName} - ${shipName}`);
        } else {
          debug.log(`[AdminService] No changes needed for ${cruiseLineName} - ${shipName}`);
        }
      } else {
        errors.push({
          row: 0,
          message: `Cruise line document "${docId}" not found in cruiseMetadata collection`
        });
      }
    } catch (error) {
      console.error(`[AdminService] Error updating ship metadata for ${key}:`, error);
      errors.push({
        row: 0,
        message: `Error updating ship metadata for ${cruiseLineName} - ${shipName}: ${error instanceof Error ? error.message : 'Unknown error'}`
      });
    }
  }
}

/**
 * Renames a ship's name globally across all official itineraries, summaries, metadata, and user cruises.
 */
export const renameShipGlobally = async (
  cruiseLine: string,
  oldShipName: string,
  newShipName: string,
  renameUserCruises: boolean
): Promise<{
  itinerariesRenamed: number;
  summariesRenamed: number;
  metadataUpdated: boolean;
  userCruisesRenamed: number;
}> => {
  let itinerariesRenamed = 0;
  let summariesRenamed = 0;
  let metadataUpdated = false;
  let userCruisesRenamed = 0;

  // 1. Update cruiseMetadata
  const qMetadata = query(collection(db, 'cruiseMetadata'), where('name', '==', cruiseLine));
  const metadataSnap = await getDocs(qMetadata);
  if (!metadataSnap.empty) {
    const metaDoc = metadataSnap.docs[0];
    const metaData = metaDoc.data();
    const ships: string[] = metaData.ships || [];
    const retiredShips: string[] = metaData.retiredShips || [];
    const shipMetadata = metaData.shipMetadata || {};
    const shipImages = metaData.shipImages || {};

    let changed = false;

    // Replace in ships array
    const shipIdx = ships.indexOf(oldShipName);
    const targetIdx = ships.indexOf(newShipName);
    if (shipIdx !== -1) {
      if (targetIdx !== -1) {
        // Target already exists, remove the old one (merge)
        ships.splice(shipIdx, 1);
      } else {
        // Simple rename
        ships[shipIdx] = newShipName;
      }
      ships.sort();
      changed = true;
    }

    // Replace in retiredShips array
    const retiredIdx = retiredShips.indexOf(oldShipName);
    const targetRetiredIdx = retiredShips.indexOf(newShipName);
    if (retiredIdx !== -1) {
      if (targetRetiredIdx !== -1) {
        // Target already exists, remove the old one (merge)
        retiredShips.splice(retiredIdx, 1);
      } else {
        // Simple rename
        retiredShips[retiredIdx] = newShipName;
      }
      retiredShips.sort();
      changed = true;
    }

    // Replace key in shipMetadata map
    if (shipMetadata[oldShipName]) {
      // If target exists, only copy fields that aren't already set on the target
      shipMetadata[newShipName] = {
        ...(shipMetadata[oldShipName] || {}),
        ...(shipMetadata[newShipName] || {})
      };
      delete shipMetadata[oldShipName];
      changed = true;
    }

    // Replace key in shipImages map
    if (shipImages[oldShipName]) {
      // Keep target image if it exists, otherwise copy
      if (!shipImages[newShipName]) {
        shipImages[newShipName] = shipImages[oldShipName];
      }
      delete shipImages[oldShipName];
      changed = true;
    }

    if (changed) {
      await updateDoc(doc(db, 'cruiseMetadata', metaDoc.id), {
        ships,
        retiredShips,
        shipMetadata,
        shipImages
      });
      metadataUpdated = true;
    }
  }

  // 2. Update official itineraries
  // Copy to new doc ID and delete old doc IDs
  const qItin = query(
    collection(db, 'itineraries'),
    where('cruiseLine', '==', cruiseLine),
    where('shipName', '==', oldShipName)
  );
  const itinSnap = await getDocs(qItin);
  if (!itinSnap.empty) {
    const chunks: any[] = [];
    const deleteIds: string[] = [];
    
    itinSnap.docs.forEach(docSnap => {
      const data = docSnap.data();
      const sailingDate = data.sailingDate;
      const returnDate = data.returnDate;
      
      const newDocId = getItineraryDocId(cruiseLine, newShipName, sailingDate, returnDate);
      chunks.push({
        newDocId,
        data: {
          ...data,
          shipName: newShipName,
          lastUpdated: new Date()
        }
      });
      deleteIds.push(docSnap.id);
    });

    // Write new itineraries
    const writeBatches = [];
    let currentBatch = writeBatch(db);
    let opCount = 0;

    for (const item of chunks) {
      currentBatch.set(doc(db, 'itineraries', item.newDocId), item.data);
      opCount++;
      if (opCount === 500) {
        writeBatches.push(currentBatch.commit());
        currentBatch = writeBatch(db);
        opCount = 0;
      }
    }
    if (opCount > 0) {
      writeBatches.push(currentBatch.commit());
    }
    await Promise.all(writeBatches);
    itinerariesRenamed = chunks.length;

    // Delete old itineraries
    const deleteBatches = [];
    currentBatch = writeBatch(db);
    opCount = 0;

    for (const oldId of deleteIds) {
      currentBatch.delete(doc(db, 'itineraries', oldId));
      opCount++;
      if (opCount === 500) {
        deleteBatches.push(currentBatch.commit());
        currentBatch = writeBatch(db);
        opCount = 0;
      }
    }
    if (opCount > 0) {
      deleteBatches.push(currentBatch.commit());
    }
    await Promise.all(deleteBatches);
  }

  // 3. Update itinerarySummaries
  const oldSummaryId = `${cruiseLine}_${oldShipName}`;
  const newSummaryId = `${cruiseLine}_${newShipName}`;
  const oldSummaryDoc = await getDoc(doc(db, 'itinerarySummaries', oldSummaryId));
  if (oldSummaryDoc.exists()) {
    const summaryData = oldSummaryDoc.data() as ItinerarySummary;
    const newSummaryRef = doc(db, 'itinerarySummaries', newSummaryId);
    const newSummaryDoc = await getDoc(newSummaryRef);
    if (newSummaryDoc.exists()) {
      // Merge!
      const newSummaryData = newSummaryDoc.data() as ItinerarySummary;
      const combinedDates = Array.from(new Set([
        ...(newSummaryData.departureDates || []),
        ...(summaryData.departureDates || [])
      ])).sort();
      const combinedDetails = {
        ...(newSummaryData.itineraryDetails || {}),
        ...(summaryData.itineraryDetails || {})
      };
      await setDoc(newSummaryRef, {
        ...newSummaryData,
        departureDates: combinedDates,
        itineraryDetails: combinedDetails,
        lastUpdated: new Date()
      }, { merge: true });
    } else {
      // Copy
      await setDoc(newSummaryRef, {
        ...summaryData,
        shipName: newShipName,
        lastUpdated: new Date()
      });
    }
    await deleteDoc(doc(db, 'itinerarySummaries', oldSummaryId));
    summariesRenamed = 1;
  }

  // 4. Update user cruises
  if (renameUserCruises) {
    // Import dynamically to avoid circular dependencies
    const { massRenameUserCruises } = await import('./dataIntegrityService');
    userCruisesRenamed = await massRenameUserCruises(cruiseLine, oldShipName, newShipName);
  }

  return {
    itinerariesRenamed,
    summariesRenamed,
    metadataUpdated,
    userCruisesRenamed
  };
};

/**
 * Mass deletes itineraries by ship.
 */
export const deleteShipItinerariesGlobally = async (
  cruiseLine: string,
  shipName: string,
  removeShipFromMetadata: boolean
): Promise<{
  itinerariesDeleted: number;
  summariesDeleted: number;
  metadataUpdated: boolean;
}> => {
  let itinerariesDeleted = 0;
  let summariesDeleted = 0;
  let metadataUpdated = false;

  // 1. Delete official itineraries
  const qItin = query(
    collection(db, 'itineraries'),
    where('cruiseLine', '==', cruiseLine),
    where('shipName', '==', shipName)
  );
  const itinSnap = await getDocs(qItin);
  if (!itinSnap.empty) {
    const ids = itinSnap.docs.map(d => d.id);
    const batches = [];
    let currentBatch = writeBatch(db);
    let opCount = 0;

    for (const id of ids) {
      currentBatch.delete(doc(db, 'itineraries', id));
      opCount++;
      if (opCount === 500) {
        batches.push(currentBatch.commit());
        currentBatch = writeBatch(db);
        opCount = 0;
      }
    }
    if (opCount > 0) {
      batches.push(currentBatch.commit());
    }
    await Promise.all(batches);
    itinerariesDeleted = ids.length;
  }

  // 2. Delete itinerarySummaries
  const summaryId = `${cruiseLine}_${shipName}`;
  const summaryRef = doc(db, 'itinerarySummaries', summaryId);
  const summaryDoc = await getDoc(summaryRef);
  if (summaryDoc.exists()) {
    await deleteDoc(summaryRef);
    summariesDeleted = 1;
  }

  // 3. Remove ship from metadata
  if (removeShipFromMetadata) {
    const qMetadata = query(collection(db, 'cruiseMetadata'), where('name', '==', cruiseLine));
    const metadataSnap = await getDocs(qMetadata);
    if (!metadataSnap.empty) {
      const metaDoc = metadataSnap.docs[0];
      const metaData = metaDoc.data();
      const ships: string[] = metaData.ships || [];
      const retiredShips: string[] = metaData.retiredShips || [];
      const shipMetadata = metaData.shipMetadata || {};
      const shipImages = metaData.shipImages || {};

      let changed = false;

      const shipIdx = ships.indexOf(shipName);
      if (shipIdx !== -1) {
        ships.splice(shipIdx, 1);
        changed = true;
      }

      const retiredIdx = retiredShips.indexOf(shipName);
      if (retiredIdx !== -1) {
        retiredShips.splice(retiredIdx, 1);
        changed = true;
      }

      if (shipMetadata[shipName]) {
        delete shipMetadata[shipName];
        changed = true;
      }

      if (shipImages[shipName]) {
        delete shipImages[shipName];
        changed = true;
      }

      if (changed) {
        await updateDoc(doc(db, 'cruiseMetadata', metaDoc.id), {
          ships,
          retiredShips,
          shipMetadata,
          shipImages
        });
        metadataUpdated = true;
      }
    }
  }

  return {
    itinerariesDeleted,
    summariesDeleted,
    metadataUpdated
  };
};

/**
 * Merges official itinerary data into a user's manually entered cruise.
 * Preserves user excursions, notes, and other customizations.
 */
export const alignCruiseToOfficial = async (
  cruiseId: string,
  officialItinerary: Itinerary
): Promise<void> => {
  const cruiseRef = doc(db, 'cruises', cruiseId);
  const cruiseSnap = await getDoc(cruiseRef);
  
  if (!cruiseSnap.exists()) {
    throw new Error(`User cruise ${cruiseId} not found`);
  }
  
  const cruiseData = cruiseSnap.data() as Cruise;
  const userPorts = cruiseData.ports || [];
  const officialPorts = officialItinerary.ports || [];
  
  // Merge ports by date, tracking matched user port indices to handle multiple ports on the same date and heal corrupt duplicate port IDs
  const matchedIndices = new Set<number>();

  const mergedPorts = officialPorts.map((offPort, idx) => {
    // 1. Try to find a user port matching both date and name exactly
    const exactMatchIdx = userPorts.findIndex((up, uIdx) => 
      up.date === offPort.date && 
      up.name.trim().toLowerCase() === offPort.name.trim().toLowerCase() &&
      !matchedIndices.has(uIdx)
    );
    
    // 2. Fallback to matching by date only
    const fallbackIdx = userPorts.findIndex((up, uIdx) => 
      up.date === offPort.date && 
      !matchedIndices.has(uIdx)
    );
    
    const matchingUserPortIdx = exactMatchIdx !== -1 ? exactMatchIdx : fallbackIdx;
    let matchingUserPort = null;
    
    if (matchingUserPortIdx !== -1) {
      matchedIndices.add(matchingUserPortIdx);
      matchingUserPort = userPorts[matchingUserPortIdx];
    }
    
    // Heal duplicate IDs: if the matched user port shares an ID with another port in the array, generate a new unique ID
    const isDuplicateId = matchingUserPort && userPorts.some((p, pIdx) => 
      p.id === matchingUserPort.id && pIdx !== matchingUserPortIdx
    );

    return {
      id: (matchingUserPort && !isDuplicateId) 
        ? matchingUserPort.id 
        : `port_${idx}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      name: offPort.name,
      date: offPort.date,
      arrivalTime: offPort.arrivalTime || '',
      departureTime: offPort.departureTime || '',
      excursion: matchingUserPort?.excursion || '',
      fromItinerary: true
    };
  });
  
  // Keep any user custom ports that are NOT on the official dates
  // (e.g. extra hotel stays, user additions)
  userPorts.forEach((up, uIdx) => {
    // A user port is custom if it wasn't matched to any official port
    if (!matchedIndices.has(uIdx)) {
      mergedPorts.push({
        id: up.id,
        name: up.name,
        date: up.date,
        arrivalTime: up.arrivalTime || '',
        departureTime: up.departureTime || '',
        excursion: up.excursion || '',
        fromItinerary: false // mark custom ports as false
      });
    }
  });

  // Sort merged ports by date
  mergedPorts.sort((a, b) => a.date.localeCompare(b.date));

  // Update user cruise document
  await updateDoc(cruiseRef, {
    departurePort: officialItinerary.departurePort,
    returnDate: officialItinerary.returnDate,
    ports: mergedPorts,
    lastUpdated: new Date()
  });
};

/**
 * Flags a user's cruise document to ignore reconciliation recommendations.
 */
export const ignoreCruiseReconciliation = async (cruiseId: string): Promise<void> => {
  const cruiseRef = doc(db, 'cruises', cruiseId);
  await updateDoc(cruiseRef, { reconciliationIgnored: true });
};

/**
 * Fan-out helper to sync linked user cruises during manual admin itinerary uploads
 */
async function syncUserCruisesForOfficialItinerary(
  itineraryDocId: string,
  row: CsvItineraryRow
): Promise<void> {
  const linkedCruisesQuery = query(
    collection(db, 'cruises'),
    where('itineraryKey', '==', itineraryDocId)
  );
  const linkedSnap = await getDocs(linkedCruisesQuery);
  if (linkedSnap.empty) return;

  const officialPorts = row.portsOfCall || [];
  const now = Date.now();
  const batch = writeBatch(db);

  linkedSnap.docs.forEach(cruiseDoc => {
    const cruise = cruiseDoc.data() as Cruise;
    const userPorts: any[] = cruise.ports || [];
    const matchedIndices = new Set<number>();

    const mergedPorts = officialPorts.map((offPort, idx) => {
      const exactMatchIdx = userPorts.findIndex((up, uIdx) => 
        up.date === offPort.date && 
        up.name.trim().toLowerCase() === offPort.name.trim().toLowerCase() &&
        !matchedIndices.has(uIdx)
      );

      const fallbackIdx = userPorts.findIndex((up, uIdx) => 
        up.date === offPort.date && 
        !matchedIndices.has(uIdx)
      );

      const matchingUserPortIdx = exactMatchIdx !== -1 ? exactMatchIdx : fallbackIdx;
      let matchingUserPort = null;

      if (matchingUserPortIdx !== -1) {
        matchedIndices.add(matchingUserPortIdx);
        matchingUserPort = userPorts[matchingUserPortIdx];
      }

      return {
        id: matchingUserPort?.id || `port_${idx}_${now}`,
        name: offPort.name,
        date: offPort.date,
        arrivalTime: offPort.arrivalTime || '',
        departureTime: offPort.departureTime || '',
        excursion: matchingUserPort?.excursion || '',
        fromItinerary: true
      };
    });

    userPorts.forEach((up, uIdx) => {
      if (!matchedIndices.has(uIdx) && up.fromItinerary === false) {
        mergedPorts.push({
          id: up.id || `custom_port_${uIdx}`,
          name: up.name,
          date: up.date,
          arrivalTime: up.arrivalTime || '',
          departureTime: up.departureTime || '',
          excursion: up.excursion || '',
          fromItinerary: false
        });
      }
    });

    mergedPorts.sort((a, b) => a.date.localeCompare(b.date));

    const updates: any = {
      ports: mergedPorts,
      itinerarySyncedAt: now
    };
    if (row.departurePort) {
      updates.departurePort = row.departurePort;
    }

    batch.update(cruiseDoc.ref, updates);
  });

  await batch.commit();
}


